<template>
  <component
    :is="as || 'span'"
    :class="['typography', presetClass, uppercaseClass]"
    :style="customStyles"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
interface Props {
  preset?: string,
  as?: string,
  customStyles?: Record<string, string>,
  uppercase?: boolean,
}

const { preset = '', as = 'span', customStyles, uppercase = false } = defineProps<Props>();

const presetClass = computed(() => preset ? `typography--${preset}` : '');
const uppercaseClass = computed(() => uppercase ? 'typography--uppercase' : '');
</script>

<style lang="scss" scoped>
// Базовые стили
.typography {
  margin: 0;
  padding: 0;
  font-family: $font-default;

  &--uppercase {
    text-transform: uppercase;
  }

  &--title {
    color: $color-white;
    text-align: center;
    font-size: 5.2rem;
    font-weight: 900;
    line-height: 83%;
    letter-spacing: -0.104rem;
    text-transform: uppercase;

    @include layout-aspect-mobile {
      font-size: 4.4rem;
    }
  }

  &--subtitle {
    color: $color-white;
    text-align: center;
    font-size: 3.2rem;
    font-style: normal;
    font-weight: 700;
    line-height: 95%; /* 3.04rem */
    letter-spacing: -0.064rem;
  }

  // H1 desktop: 56px / 95%, mobile: 40px / 95%
  &--heading-xl {
    font-weight: $font-weight-black;
    font-size: 5.6rem;
    line-height: 0.95;

    @include layout-aspect-mobile {
      font-size: 4rem;
      line-height: 0.95;
    }
  }

  // H2 desktop: 48px / 95%, mobile: 36px / 95%
  &--heading-l {
    font-weight: $font-weight-black;
    font-size: 4.8rem;
    line-height: 0.95;

    @include layout-aspect-mobile {
      font-size: 3.6rem;
      line-height: 0.95;
    }
  }

  // H3 desktop: 40px / 95%, mobile: 32px / 95%
  &--heading-m {
    font-weight: $font-weight-black;
    font-size: 4rem;
    line-height: 0.95;

    @include layout-aspect-mobile {
      font-size: 3.2rem;
      line-height: 0.95;
    }
  }

  // H4 desktop: 32px / 105%, mobile: 24px / 105%
  &--heading-s {
    font-weight: $font-weight-bold;
    font-size: 3.2rem;
    line-height: 1.05;

    @include layout-aspect-mobile {
      font-size: 2.4rem;
      line-height: 1.05;
    }
  }
  &--heading-m {
    font-weight: $font-weight-black;
    font-size: 2.4rem;
    line-height: 95%;

    @include layout-aspect-mobile {
      font-size: 2.4rem;
      line-height: 1.05;
    }
  }

  // Bold Big Text desktop: 20px / 115%, mobile: 20px / 125%
  &--text-m {
    font-weight: $font-weight-black;
    font-size: 2rem;
    line-height: 1.15;

    @include layout-aspect-mobile {
      line-height: 1.25;
    }
  }

  // Text desktop: 16px / 125%, mobile: 16px / 125%
  &--text-s {
    font-weight: $font-weight-medium;
    font-size: 1.6rem;
    // font-size: 1.2rem;
    line-height: 1.25;
  }

  &--text-xs {
    font-weight: $font-weight-medium;
    font-size: 1.2rem;
    line-height: 125%;
    letter-spacing: -0.048rem;
    opacity: .6;
  }
}
</style>
